// The PO panel's Make tab for make POs: each line is a build at the CM from a BOM snapshot. Shows the consigned
// components it needs (BOM quantity × units + scrap allowance), what's reserved and short at the CM, progress
// (built, delivered, waiting at the CM), and reports output in parts (components consumed at the CM) and scrap. Each
// build's parts and reports are the usual table (DataTable: views, columns, Copy / CSV).
import { Factory, Plus, RefreshCw } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { SUPPLY_LABELS } from "../../../../shared/boms.ts";
import { MAKE_STAGE_LABELS, MAKE_STAGES, type MakeOrderState, SCRAP_REASON_LABELS, type ScrapReason } from "../../../../shared/consignment.ts";
import type { CmReport, Item, MakeOrder, Po, PoLine } from "../../../../shared/db.gen.ts";
import { linesEditable } from "../../../../shared/po.ts";
import { backersLeft, hhlDone, outstanding } from "../../../../shared/receiving.ts";
import { bomPath, useBomIndex } from "../../lib/boms.ts";
import { ago, qty } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { Link } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, Button, cx, Input, Select, Thumb } from "../../ui/controls.tsx";
import { type Column, DataTable } from "../../ui/DataTable.tsx";
import { toast } from "../../ui/toast.tsx";
import { LineCharges } from "../Charges.tsx"; // Charges ---
import { consignmentPath, makeOrderStates, STATUS_LABELS, STATUS_TONES, useCms } from "./lib.ts";
import { ItemPicker, parseQty, QtyInput } from "./parts.tsx";
import { reasonOptions } from "./ScrapDialog.tsx";

const REPORTABLE = new Set(["sent", "acknowledged", "in_production", "shipped"]);

export function MakeOrderView({ po, lines }: { po: Po; lines: PoLine[] }) {
  const makeOrders = useRows("make_order");
  const locations = useRows("location");
  const cms = useCms();
  const states = useMemo(() => makeOrderStates(cms), [cms]);
  const byLine = useMemo(() => new Map(makeOrders.filter((m) => m.po_id === po.id).map((m) => [m.po_line_id, m])), [makeOrders, po.id]);
  const cmId = [...byLine.values()][0]?.cm_location_id;
  const cm = locations.find((l) => l.id === cmId) ?? locations.find((l) => l.is_cm && l.vendor_id === po.vendor_id);
  const editable = linesEditable(po.status) && can("make_order.create");

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="flex items-center gap-2 border-b border-line px-4 py-2 text-[12px] text-muted">
        <Factory className="size-4 text-faint" />
        {cm ? (
          <>
            Built at{" "}
            <Link to={consignmentPath(cm.id)} className="font-medium text-text hover:underline">
              {cm.name}
            </Link>{" "}
            from HHL's consigned parts there.
          </>
        ) : (
          <span className="text-warn">This vendor has no CM site yet: set one up on the Consignment page.</span>
        )}
        <span className="ml-auto">
          {po.status === "draft" ? "Mark it as sent to report output." : REPORTABLE.has(po.status) ? "" : "Output can't be reported on this PO any more."}
        </span>
      </div>
      <BackersBar po={po} lines={lines} byLine={byLine} />
      {!lines.length && <div className="p-6 text-center text-muted">No builds yet{editable ? ": add what the CM should build below" : ""}.</div>}
      {lines.map((line) => {
        const mo = byLine.get(line.id);
        return mo ? (
          <Build key={line.id} po={po} line={line} mo={mo} state={states.get(mo.id)} />
        ) : (
          <NoSnapshot key={line.id} po={po} line={line} editable={editable} />
        );
      })}
      {editable && cm && <AddBuild po={po} />}
    </div>
  );
}

/**
 * Pre-sales --- the units for backers the CM still has to build or ship: the PO stays open for them (received once
 * they're reported built or shipped). If the CM won't build them, staff release them back to planning.
 */
function BackersBar({ po, lines, byLine }: { po: Po; lines: PoLine[]; byLine: Map<string, MakeOrder> }) {
  const vendors = useRows("vendor");
  const [busy, setBusy] = useState(false);
  const counted = lines.map((l) => ({ ...l, hhl_closed: po.hhl_share_closed, qty_output: byLine.get(l.id)?.qty_output ?? null }));
  const left = counted.reduce((t, l) => t + backersLeft(l), 0);
  if (left <= 0 || ["draft", "received", "closed", "cancelled"].includes(po.status)) return null;
  const v = vendors.find((x) => x.id === po.vendor_id);
  const cm = v?.short_name || v?.name || "The CM";
  const due = counted.filter((l) => !hhlDone(l)).reduce((t, l) => t + outstanding(l), 0);
  const release = async () => {
    if (
      !confirm(
        `${cm} won't build the other ${qty(left)} for backers on ${po.number}?\n\nThey go back to planning: another open make PO on the run takes them, else they show as still to build. ${po.number} is received with what's built or shipped for backers so far. This can't be undone here.`,
      )
    )
      return;
    setBusy(true);
    const { ok, result } = await mutateWithResult("po.release_backers", { id: po.id });
    setBusy(false);
    if (ok) toast.ok((result as { note?: string })?.note ?? "Released");
  };
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface-2/50 px-4 py-2 text-[12px]">
      <span>
        <span className="font-medium">
          {cm} still has {qty(left)} to build or ship for backers.
        </span>{" "}
        <span className="text-muted">
          {po.number} stays open until they're reported built or shipped
          {po.hhl_share_closed ? "; nothing else is coming to HHL" : due > 0 ? `; ${qty(due)} still expected at HHL` : "; everything for HHL is in"}.
        </span>
      </span>
      {po.hhl_share_closed && can("po.reopen_hhl_share") && (
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto"
          disabled={busy}
          title="Undo Nothing else is coming: HHL expects the rest of its share again (and Shopify shows it as incoming)"
          onClick={async () => {
            setBusy(true);
            const { ok } = await mutateWithResult("po.reopen_hhl_share", { id: po.id });
            setBusy(false);
            if (ok) toast.ok("HHL's share is open again");
          }}
        >
          Reopen HHL's share
        </Button>
      )}
      {can("po.release_backers") && (
        <Button
          size="sm"
          variant="ghost"
          className={po.hhl_share_closed ? "" : "ml-auto"}
          disabled={busy || due > 0}
          title={
            due > 0
              ? "Receive HHL's share first (or post a receipt with Nothing else is coming)"
              : "They go back to planning: another make PO, else still to build"
          }
          onClick={release}
        >
          {cm} won't build them
        </Button>
      )}
    </div>
  );
}

function NoSnapshot({ po, line, editable }: { po: Po; line: PoLine; editable: boolean }) {
  const [id] = useState(() => crypto.randomUUID());
  const use = async () => {
    const { ok, result } = await mutateWithResult("make_order.create", { id, po_id: po.id, po_line_id: line.id });
    if (ok) for (const w of (result as { warnings?: string[] }).warnings ?? []) toast.info(w);
  };
  return (
    <div className="flex items-center gap-3 border-b border-line px-4 py-3">
      <span className="font-mono text-[12px]">{line.sku}</span>
      <span className="min-w-0 flex-1 truncate">{line.name}</span>
      <span className="text-[12px] text-warn">No BOM snapshot: the CM's parts for it aren't reserved or consumed</span>
      {editable && (
        <Button size="sm" onClick={use}>
          Use its BOM
        </Button>
      )}
    </div>
  );
}

function AddBuild({ po }: { po: Po }) {
  const ix = useBomIndex();
  const [item, setItem] = useState<Item | null>(null);
  const [n, setN] = useState("");
  const [busy, setBusy] = useState(false);
  const hasBom = (i: Item) => ix.families.has(i.source === "shopify" && i.shopify_product_id ? `product:${i.shopify_product_id}` : `item:${i.id}`);
  const add = async () => {
    const count = parseQty(n);
    if (!item || !count || count <= 0) return toast.error("Pick an item and how many");
    setBusy(true);
    const { ok, result } = await mutateWithResult("make_order.create", { id: crypto.randomUUID(), po_id: po.id, item_id: item.id, qty: count });
    setBusy(false);
    if (!ok) return;
    for (const w of (result as { warnings?: string[] }).warnings ?? []) toast.info(w);
    setItem(null);
    setN("");
  };
  return (
    <div className="flex items-center gap-2 px-4 py-3">
      <Plus className="size-4 text-faint" />
      {item ? (
        <>
          <span className="font-mono text-[12px]">{item.sku}</span>
          <span className="truncate">{item.name}</span>
          <button type="button" className="text-[12px] text-accent-text hover:underline" onClick={() => setItem(null)}>
            change
          </button>
        </>
      ) : (
        <ItemPicker className="w-80" allow={hasBom} onPick={setItem} placeholder="Add a build: an item with a BOM…" />
      )}
      <div className="w-24">
        <QtyInput aria-label="Units to build" value={n} onChange={setN} />
      </div>
      <Button size="sm" variant="primary" disabled={busy || !item} onClick={add}>
        Add build
      </Button>
      <span className="text-[12px] text-faint">
        Its price is the vendor's for the item, else what its BOM says the CM charges (services and parts the CM buys).
      </span>
    </div>
  );
}

function Build({ po, line, mo, state }: { po: Po; line: PoLine; mo: MakeOrder; state: MakeOrderState | undefined }) {
  const reports = useRows("cm_report");
  const items = useRows("item");
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const mine = useMemo(() => reports.filter((r) => r.make_order_id === mo.id).sort((a, b) => b.reported_at.localeCompare(a.reported_at)), [reports, mo.id]);
  const item = itemById.get(mo.item_id);
  const canReport = REPORTABLE.has(po.status) && can("make_order.report_output");
  const canRefresh = Number(mo.qty_output) === 0 && !mine.length && linesEditable(po.status) && can("make_order.refresh_bom");

  const refresh = async () => {
    const { ok, result } = await mutateWithResult("make_order.refresh_bom", { id: mo.id });
    if (ok) toast.ok(`Now built from revision ${(result as { bom_revision: number }).bom_revision}`);
  };

  return (
    <div className="border-b border-line">
      <div className="flex flex-wrap items-center gap-3 px-4 pt-3">
        <Thumb src={item?.photo_url} size={36} />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">
            <span className="font-mono text-[12px] font-medium">{line.sku}</span> {line.name}
          </span>
          <span className="text-[12px] text-muted">
            {qty(line.qty)} to build ·{" "}
            {mo.bom_id ? (
              <Link to={bomPath(mo.bom_id, mo.item_id)} className="hover:underline">
                BOM revision {mo.bom_revision}
              </Link>
            ) : (
              `BOM revision ${mo.bom_revision ?? "?"}`
            )}
            {" (kept as it was when the order was made)"}
            {canRefresh && (
              <button
                type="button"
                onClick={refresh}
                className="ml-2 inline-flex items-center gap-1 text-accent-text hover:underline"
                title="Take its BOM's current revision again"
              >
                <RefreshCw className="size-3" /> use the current revision
              </button>
            )}
          </span>
        </div>
        <div className="w-40 shrink-0">
          <Select
            aria-label="Stage"
            value={mo.stage}
            disabled={!can("make_order.update")}
            onChange={(e) => mutate("make_order.update", { id: mo.id, patch: { stage: e.target.value as MakeOrder["stage"] } })}
            options={MAKE_STAGES.map((s) => ({ value: s, label: MAKE_STAGE_LABELS[s] }))}
            className="h-7 text-[12px]"
          />
        </div>
        {state && <Badge tone={STATUS_TONES[state.status]}>{STATUS_LABELS[state.status]}</Badge>}
      </div>
      {state && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 px-4 pt-2 text-[12px] text-muted">
          <span>
            Built <span className="font-mono text-text">{qty(state.output) || "0"}</span> of {qty(state.qty)}
          </span>
          <span>
            Delivered <span className="font-mono text-text">{qty(state.delivered) || "0"}</span>
          </span>
          <span>
            Waiting at the CM <span className="font-mono text-text">{qty(state.atCm) || "0"}</span>
          </span>
          {state.open && (
            <span>
              Buildable now <span className="font-mono text-text">{qty(state.buildable) || "0"}</span>
            </span>
          )}
        </div>
      )}
      <Components mo={mo} state={state} itemById={itemById} />
      <LineCharges po={po} line={line} />
      {canReport && <ReportForm mo={mo} state={state} itemById={itemById} />}
      {mine.length > 0 && <Reports reports={mine} itemById={itemById} />}
    </div>
  );
}

type ReportRow = CmReport & { scrap: string; by: string };

/** A build's reports (output, scrap), newest first: the usual table, its menus by its label. */
function Reports({ reports, itemById }: { reports: CmReport[]; itemById: Map<string, Item> }) {
  const reportLines = useRows("cm_report_line");
  const users = useRows("app_user");
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const rows = useMemo<ReportRow[]>(
    () =>
      reports.map((r) => ({
        ...r,
        scrap: reportLines
          .filter((l) => l.report_id === r.id && l.kind === "scrap")
          .map((l) => `${itemById.get(l.item_id)?.sku ?? "?"} −${qty(l.qty)} (${SCRAP_REASON_LABELS[l.reason as ScrapReason] ?? l.reason})`)
          .join(", "),
        by: r.reported_by ? (users.find((u) => u.id === r.reported_by)?.name ?? "") : "",
      })),
    [reports, reportLines, users, itemById],
  );
  const columns: Column<ReportRow>[] = [
    {
      key: "reported_at",
      header: "When",
      width: "110px",
      value: (r) => r.reported_at,
      cell: (r) => (
        <span className="truncate text-[12px] text-faint" title={new Date(r.reported_at).toLocaleString()}>
          {ago(r.reported_at)}
        </span>
      ),
    },
    {
      key: "what",
      header: "Report",
      width: "130px",
      value: (r) => (r.kind === "output" ? `${qty(r.qty_output)} built` : "Scrap"),
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-1.5 text-[12px]">
          <span className="truncate">{r.kind === "output" ? `${qty(r.qty_output)} built` : "Scrap"}</span>
          {r.auto && <Badge>from a receipt</Badge>}
        </span>
      ),
    },
    { key: "scrap", header: "Scrap", width: "minmax(160px,1.5fr)", cell: (r) => <span className="truncate text-[12px] text-muted">{r.scrap}</span> },
    {
      key: "notes",
      header: "Note",
      width: "minmax(140px,1fr)",
      cell: (r) => (
        <span className="truncate text-[12px] text-muted" title={r.notes || undefined}>
          {r.notes}
        </span>
      ),
    },
    { key: "by", header: "By", width: "120px", cell: (r) => <span className="truncate text-[12px] text-muted">{r.by}</span> },
  ];
  return (
    <div className="px-4 pb-3">
      <div className="flex items-center gap-2 pb-1">
        <span className="text-[11px] text-faint">Reports</span>
        <div ref={setSlot} className="ml-auto flex items-center gap-1" />
      </div>
      <DataTable
        fit
        rows={rows}
        columns={columns}
        defaultSort={{ key: "reported_at", desc: true }}
        rowNoun={["report", "reports"]}
        exportName="Build reports"
        viewsSlot={slot}
        pageFilters={false}
      />
    </div>
  );
}

type ComponentRow = MakeOrderState["components"][number] & { id: string; sku: string; name: string; atCm: number | null };

/** The build's parts (its BOM snapshot × units), what's used, still needed, at the CM, set aside and short: the usual table. */
function Components({ mo, state, itemById }: { mo: MakeOrder; state: MakeOrderState | undefined; itemById: Map<string, Item> }) {
  const cmStock = useCms().get(mo.cm_location_id);
  const onHand = useMemo(() => new Map((cmStock?.stock ?? []).map((l) => [l.itemId, l])), [cmStock]);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  const rows = useMemo<ComponentRow[]>(
    () =>
      (state?.components ?? []).map((c) => ({
        ...c,
        id: `${c.item_id}|${c.supply}`,
        sku: itemById.get(c.item_id)?.sku ?? c.sku,
        name: itemById.get(c.item_id)?.name ?? c.name,
        atCm: c.consigned ? (onHand.get(c.item_id)?.onHand ?? 0) : null,
      })),
    [state, itemById, onHand],
  );
  if (!state) return <div className="px-4 py-2 text-[12px] text-warn">The CM site isn't marked as one any more, so this build's parts can't be shown.</div>;
  // Parts HHL doesn't consign (the CM buys or makes them, services) are faint, as they're the CM's to have.
  const dim = (c: ComponentRow, node: ReactNode, cls = "") => (
    <span className={cx("truncate font-mono text-[12px]", cls, !c.consigned && "text-faint")}>{node}</span>
  );
  const columns: Column<ComponentRow>[] = [
    { key: "sku", header: "SKU", width: "120px", cell: (c) => dim(c, c.sku) },
    {
      key: "name",
      header: "Part",
      width: "minmax(160px,1fr)",
      cell: (c) => (
        <span className={cx("flex min-w-0 items-center gap-1", !c.consigned && "text-faint")}>
          <span className="truncate">{c.name}</span>
          {c.source === "shopify" && c.consigned && <Badge>Shopify</Badge>}
        </span>
      ),
    },
    {
      key: "supply",
      header: "Supply",
      width: "110px",
      value: (c) => (c.kind === "service" ? "Service" : SUPPLY_LABELS[c.supply]),
      cell: (c) => (
        <span className={cx("truncate text-[12px]", !c.consigned && "text-faint")}>{c.kind === "service" ? "Service" : SUPPLY_LABELS[c.supply]}</span>
      ),
    },
    { key: "per_unit", header: "Per unit", width: "80px", align: "right", value: (c) => c.per_unit, cell: (c) => dim(c, qty(c.per_unit)) },
    { key: "need", header: "Need", headerTitle: "BOM quantity × units", width: "84px", align: "right", value: (c) => c.need, cell: (c) => dim(c, qty(c.need)) },
    {
      key: "allowance",
      header: "+ scrap",
      headerTitle: "Scrap allowance × units",
      width: "80px",
      align: "right",
      value: (c) => c.allowance,
      cell: (c) => dim(c, c.allowance ? qty(c.allowance) : "", "text-muted"),
    },
    { key: "total", header: "Total", width: "84px", align: "right", value: (c) => c.total, cell: (c) => dim(c, qty(c.total)) },
    {
      key: "consumed",
      header: "Used",
      width: "80px",
      align: "right",
      value: (c) => (c.consigned ? c.consumed : null),
      cell: (c) => dim(c, c.consigned && c.consumed ? qty(c.consumed) : "", "text-muted"),
    },
    {
      key: "remaining",
      header: "Still need",
      headerTitle: "What the units still to build take",
      width: "90px",
      align: "right",
      value: (c) => (c.consigned ? c.remaining : null),
      cell: (c) => dim(c, c.consigned ? qty(c.remaining) || "0" : ""),
    },
    {
      key: "atCm",
      header: "At CM",
      width: "84px",
      align: "right",
      value: (c) => c.atCm,
      cell: (c) => dim(c, c.atCm == null ? "" : qty(c.atCm) || "0", "text-muted"),
    },
    {
      key: "reserved",
      header: "Reserved",
      headerTitle: "Set aside for this order from the CM's stock, in priority order",
      width: "90px",
      align: "right",
      value: (c) => (c.consigned ? c.reserved : null),
      cell: (c) => dim(c, c.consigned && c.reserved ? qty(c.reserved) : "", "text-muted"),
    },
    {
      key: "short",
      header: "Short",
      width: "90px",
      align: "right",
      value: (c) => (c.short > 0 ? c.short : null),
      text: (c) => (c.short > 0 ? c.short : c.shortConfirmed > 0 ? "in transit" : ""),
      cell: (c) => (
        <span className={cx("truncate font-mono text-[12px]", c.short > 0 ? "text-danger" : c.shortConfirmed > 0 ? "text-accent-text" : "")}>
          {c.short > 0 ? qty(c.short) : c.shortConfirmed > 0 ? <span title="Covered once what's in transit arrives">in transit</span> : ""}
        </span>
      ),
    },
    // The BOM's order (what it's sorted by until someone sorts it another way).
    { key: "position", header: "#", optional: true, width: "48px", align: "right", value: (c) => rows.indexOf(c) + 1 },
  ];
  return (
    <div className="px-4 pt-2">
      <div className="flex items-center gap-2 pb-1">
        <span className="text-[11px] text-faint">Parts</span>
        <div ref={setSlot} className="ml-auto flex items-center gap-1" />
      </div>
      <DataTable
        fit
        rows={rows}
        columns={columns}
        defaultSort={{ key: "position" }}
        rowNoun={["part", "parts"]}
        exportName="Build parts"
        viewsSlot={slot}
        pageFilters={false}
      />
    </div>
  );
}

function ReportForm({ mo, state, itemById }: { mo: MakeOrder; state: MakeOrderState | undefined; itemById: Map<string, Item> }) {
  const [n, setN] = useState("");
  const [notes, setNotes] = useState("");
  const [scrap, setScrap] = useState<{ item: Item; qty: string; reason: ScrapReason }[]>([]);
  const [busy, setBusy] = useState(false);
  const [id, setId] = useState(() => crypto.randomUUID());
  const parts = useMemo(() => new Set((state?.components ?? []).filter((c) => c.consigned).map((c) => c.item_id)), [state]);

  const submit = async (allowOver = false) => {
    const count = parseQty(n);
    if (!count || count <= 0 || !Number.isInteger(count)) return toast.error("Enter the whole units built");
    const bad = scrap.find((s) => !((parseQty(s.qty) ?? 0) > 0));
    if (bad) return toast.error(`Enter how many ${bad.item.sku} were scrapped`);
    const over = state && state.output + count > state.qty;
    if (over && !allowOver) {
      if (!confirm(`That makes ${qty(state.output + count)} built of ${qty(state.qty)} ordered. Report it anyway?`)) return;
      return submit(true);
    }
    setBusy(true);
    const { ok, result } = await mutateWithResult("make_order.report_output", {
      id,
      make_order_id: mo.id,
      qty: count,
      notes: notes.trim(),
      allow_over: allowOver || undefined,
      scrap: scrap.length ? scrap.map((s) => ({ item_id: s.item.id, qty: parseQty(s.qty) as number, reason: s.reason })) : undefined,
    });
    setBusy(false);
    if (!ok) return;
    const r = result as { output: number; warnings?: string[] };
    toast.ok(`${qty(r.output)} built so far`);
    for (const w of r.warnings ?? []) toast.info(w);
    setN("");
    setNotes("");
    setScrap([]);
    setId(crypto.randomUUID());
  };

  return (
    <div className="mx-4 my-2 rounded-md border border-line bg-surface-2/40 p-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[12px] text-muted">The CM built</span>
        <div className="w-24">
          <QtyInput aria-label="Units built" value={n} onChange={setN} />
        </div>
        <span className="text-[12px] text-muted">more</span>
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Note (their report, batch)" className="h-7 min-w-40 flex-1 text-[12px]" />
        <ItemPicker
          className="w-56"
          allow={(i) => parts.has(i.id) && !scrap.some((s) => s.item.id === i.id)}
          onPick={(item) => setScrap((s) => [...s, { item, qty: "", reason: "defective" }])}
          placeholder="+ scrap a part…"
        />
        <Button size="sm" variant="primary" disabled={busy} onClick={() => submit()} title="Consumes its parts at the CM and records the finished goods there">
          Report output
        </Button>
      </div>
      {scrap.map((s) => (
        <div key={s.item.id} className="mt-1 flex items-center gap-2 text-[12px]">
          <span className="w-28 truncate font-mono">{itemById.get(s.item.id)?.sku}</span>
          <span className="min-w-0 flex-1 truncate">{s.item.name}</span>
          <span className="text-muted">scrapped beyond the allowance</span>
          <div className="w-20">
            <QtyInput
              aria-label={`${s.item.sku} scrapped`}
              value={s.qty}
              onChange={(t) => setScrap((all) => all.map((x) => (x.item.id === s.item.id ? { ...x, qty: t } : x)))}
            />
          </div>
          <div className="w-36 shrink-0">
            <Select
              aria-label="Reason"
              value={s.reason}
              onChange={(e) => setScrap((all) => all.map((x) => (x.item.id === s.item.id ? { ...x, reason: e.target.value as ScrapReason } : x)))}
              options={reasonOptions}
              className="h-7 text-[12px]"
            />
          </div>
          <button type="button" className="text-faint hover:text-danger" onClick={() => setScrap((all) => all.filter((x) => x.item.id !== s.item.id))}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}
